/**
 * Named enum of all possible validation error keys.
 * Each value is an i18n key to be translated in the template via $t().
 */
export const DATE_RANGE_ERRORS = {
  END_BEFORE_START: 'ppv.errors.end_date_after_start',
  EXCEEDS_MAX_DAYS: 'ppv.errors.exceed_max_days',
  START_DATE_TOO_OLD: 'ppv.errors.start_date_too_old',
}

export function useDateRangeValidation(maxRangeDays = 90) {
  const DAY_MS = 1000 * 60 * 60 * 24

  function todayStr() {
    const d = new Date()
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')

    return `${yyyy}-${mm}-${dd}`
  }

  function calcMaxStartDate() {
    return todayStr()
  }

  function calcAbsoluteMinDate() {
    const d = new Date()
    d.setDate(d.getDate() - (maxRangeDays - 1))

    return d.toISOString().slice(0, 10)
  }

  function calcMinStartDate(endDateStr) {
    const absoluteMin = calcAbsoluteMinDate()

    if (!endDateStr) {
      return absoluteMin
    }

    const end = new Date(endDateStr)
    const minByRange = new Date(end)
    minByRange.setDate(end.getDate() - (maxRangeDays - 1))

    const minByRangeStr = minByRange.toISOString().slice(0, 10)

    return minByRangeStr > absoluteMin ? minByRangeStr : absoluteMin
  }

  function calcMaxEndDate(startDateStr) {
    const today = todayStr()

    if (!startDateStr) {
      return today
    }

    const start = new Date(startDateStr)
    const maxByRange = new Date(start)
    maxByRange.setDate(start.getDate() + (maxRangeDays - 1))

    const maxByRangeStr = maxByRange.toISOString().slice(0, 10)

    return maxByRangeStr < today ? maxByRangeStr : today
  }

  /**
   * Validates the date range.
   * @returns {string} An error key from DATE_RANGE_ERRORS, or '' if valid.
   *                   Translate in the template: $t(validate(from, to))
   */
  function validate(from, to) {
    if (!from && !to) {
      return ''
    }

    if (from) {
      const absoluteMinStr = calcAbsoluteMinDate()
      const fromStr = from.toISOString().slice(0, 10)

      if (fromStr < absoluteMinStr) {
        return DATE_RANGE_ERRORS.START_DATE_TOO_OLD
      }
    }

    if (!from || !to) {
      return ''
    }

    const fromStr = from.toISOString().slice(0, 10)
    const toStr = to.toISOString().slice(0, 10)

    if (toStr < fromStr) {
      return DATE_RANGE_ERRORS.END_BEFORE_START
    }

    const diffDays = (to - from) / DAY_MS

    if (diffDays >= maxRangeDays) {
      return DATE_RANGE_ERRORS.EXCEEDS_MAX_DAYS
    }

    return ''
  }

  return {
    calcMaxStartDate,
    calcMinStartDate,
    calcMaxEndDate,
    validate
  }
}
